/**
 * @file 埋点渠道来源参数（entryChannel/entryScene/wxScene）
 * @description 小程序内嵌 H5 时随公共参数透传的埋点渠道信息，仅用于统计分析，
 * 与业务渠道 sourceFlag 完全解耦（互不读写对方存储），禁止写入/复用 sourceFlag。
 * 注意：URL 透传与 sessionStorage 存储均用 entryChannel，上报接口入参名为 referer。
 * 对接方案见 journey-client-wx 仓库 docs/plans/埋点渠道追踪/开发方案.md 2.5 节。
 */

import qs from 'qs'

// 存储 key 与小程序侧保持同名，会话级生命周期与小程序冷启动覆盖语义对齐
const STORAGE_KEY = 'channelSourceInfo'

/**
 * 合并解析 location.search 与 hash 中的 query（hash 路由场景参数可能在 # 之后）
 * @returns {Object} URL 参数对象，search 优先于 hash
 */
function parseUrlParams() {
  const { search = '', hash = '' } = window.location
  const fromSearch = qs.parse(search.replace('?', '')) || {}
  let fromHash = {}
  if (hash && hash.indexOf('?') !== -1) {
    fromHash = qs.parse(hash.split('?')[1] || '') || {}
  }
  return { ...fromHash, ...fromSearch }
}

/**
 * 入口采集埋点渠道参数（应用启动时调用一次）
 * - 仅在 URL 显式携带 client=wxapp 时生效，避免独立 H5 访问误用
 * - entryChannel/entryScene 仅在携带新值时覆盖，wxScene 每次刷新（与小程序侧热启动语义对齐）
 * - 全程 try/catch 兜底，异常不阻塞启动流程
 */
export function captureChannelSource() {
  try {
    const { client = '', entryChannel = '', entryScene = '', wxScene = '' } = parseUrlParams()
    if (client !== 'wxapp') return

    let prev = {}
    try {
      prev = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {}
    } catch {
      prev = {}
    }
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        entryChannel: String(entryChannel || prev.entryChannel || ''),
        entryScene: String(entryScene || prev.entryScene || ''),
        wxScene: String(wxScene || ''),
      })
    )
  } catch (error) {
    console.warn('采集埋点渠道参数失败:', error)
  }
}

/**
 * 读取埋点渠道参数，供埋点公共参数附加使用
 * 存储中的 entryChannel 以 referer 字段名上报（接口侧口径）
 * 异常兜底返回空值，不阻塞埋点上报
 * @returns {{ referer: string, entryScene: string, wxScene: string }}
 */
export function getChannelSourceParams() {
  try {
    const info = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {}
    return {
      referer: info.entryChannel || '',
      entryScene: info.entryScene || '',
      wxScene: info.wxScene || '',
    }
  } catch {
    return { referer: '', entryScene: '', wxScene: '' }
  }
}
